import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useCommunicationBridge } from './CommunicationBridgeProvider';

/**
 * Watches React Router's location and forwards all route changes to the
 * shell via the communication bridge. Renders nothing visible.
 */
export function NavigationSync() {
	const { pathname, search } = useLocation();
	const navigationType = useNavigationType();
	const { send, isMountedInIframe } = useCommunicationBridge();
	const isFirstRender = useRef(true);

	useEffect(() => {
		if (!isMountedInIframe) return;

		const fullPath = `${pathname}${search}`;

		if (isFirstRender.current) {
			isFirstRender.current = false;
			send({ type: 'replace-navigation', path: fullPath });
			return;
		}

		// Always send replace-navigation so the shell updates its URL without
		// pushing new history entries. The shell already receives the correct
		// history entries when the user clicks links — those navigations propagate
		// to the shell directly, but the shell won't get its URL updated without
		// replace-navigation called here. If the iframe were to send `navigate`
		// (push), the shell would accumulate duplicate history entries, causing
		// the browser back/forward buttons to desync between shell and iframe.
		if (navigationType === 'POP') {
			send({ type: 'replace-navigation', path: fullPath });
		} else if (navigationType === 'REPLACE') {
			send({ type: 'replace-navigation', path: fullPath });
		} else {
			send({ type: 'replace-navigation', path: fullPath });
		}
	}, [pathname, search, navigationType, send, isMountedInIframe]);

	return null;
}
